import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { Audio, Catalogos, Cola, Extension, Ivr } from "../../../../services/v2/telefonia.service";
import { Aviso, errorTexto } from "./comunes";
import Tablero from "./tablero";
import MiEquipo from "./miEquipo";
import Llamadas from "./llamadas";
import EnVivo from "./enVivo";
import Extensiones from "./extensiones";
import Departamentos from "./departamentos";
import Recepcion from "./recepcion";
import CampanasAuto from "./campanasAuto";
import Ajustes from "./ajustes";
import "../numeros/numeros.css";
import "./telefonia.css";

type Pestana = "tablero" | "equipo" | "llamadas" | "envivo" | "extensiones" | "departamentos" | "recepcion" | "campanas" | "ajustes";
const PESTANAS: { k: Pestana; t: string; solo?: "super" }[] = [
	{ k: "tablero", t: "Tablero" }, { k: "equipo", t: "Mi Equipo" }, { k: "llamadas", t: "Llamadas" }, { k: "envivo", t: "En vivo" }, { k: "extensiones", t: "Extensiones" },
	{ k: "departamentos", t: "Departamentos" }, { k: "recepcion", t: "Recepción" }, { k: "campanas", t: "Campañas automáticas" }, { k: "ajustes", t: "Ajustes", solo: "super" },
];

/** Lo que comparten las pestañas: catálogos y las listas que se usan para elegir destinos. */
export interface Compartido {
	campusId: number; cat: Catalogos; colas: Cola[]; ivrs: Ivr[]; extensiones: Extension[]; audios: Audio[];
	recargar: () => Promise<void>; setColas: (c: Cola[]) => void; setIvrs: (i: Ivr[]) => void; setExtensiones: (e: Extension[]) => void; setAudios: (a: Audio[]) => void;
}

/**
 * Telefonía (Mensajería; Esteban, 2026-10-08): la central telefónica administrable. Supervisión ve su territorio,
 * superadmin todo. Tablero y historial de llamadas, quién está conectado, extensiones, departamentos (colas),
 * recepción (números, recepcionista, audios, horario) y campañas automáticas del auto-marcador.
 */
const TelefoniaController: React.FC = () => {
	const http = HttpManager.getInstance();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusId: number | null = (legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey]?.id : null) ?? sessionCampus?.id ?? null;

	const [pestana, setPestana] = useState<Pestana>(() => (new URLSearchParams(window.location.search).get("t") as Pestana) || "tablero");
	const [cat, setCat] = useState<Catalogos | null>(null);
	const [colas, setColas] = useState<Cola[]>([]);
	const [ivrs, setIvrs] = useState<Ivr[]>([]);
	const [extensiones, setExtensiones] = useState<Extension[]>([]);
	const [audios, setAudios] = useState<Audio[]>([]);
	const [error, setError] = useState<string | null>(null);

	const recargar = async () => {
		if (!campusId) return;
		const [c, i, e, a] = await Promise.all([http.telefoniaService.colas(campusId), http.telefoniaService.ivrs(campusId), http.telefoniaService.extensiones(campusId), http.telefoniaService.audios(campusId)]);
		setColas(c.data.colas); setIvrs(i.data.recepcionistas); setExtensiones(e.data.extensiones); setAudios(a.data.audios);
	};
	useEffect(() => {
		if (!campusId) return;
		setError(null);
		http.telefoniaService.catalogos(campusId).then((r) => setCat(r.data)).catch((e) => setError(errorTexto(e)));
		recargar().catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	const ir = (p: Pestana) => { setPestana(p); window.history.replaceState(null, "", window.location.pathname + "?t=" + p); };

	if (!campusId) return null;
	if (error && !cat) return <div className="nu tf"><Aviso texto={error} /></div>;
	if (!cat) return <div className="nu tf"><div className="nu-vacio">Cargando…</div></div>;
	const sh: Compartido = { campusId, cat, colas, ivrs, extensiones, audios, recargar, setColas, setIvrs, setExtensiones, setAudios };

	return (
		<div className="nu tf">
			<div className="nu-cab">
				<div><h1>Telefonía</h1><span className="nu-tenue">{cat.super ? "Todos los planteles" : "Tu territorio: " + cat.planteles.map((p) => p.name).join(", ")}</span></div>
			</div>
			<div className="tf-pestanas" role="tablist">
				{PESTANAS.filter((p) => !p.solo || cat.super).map((p) => (
					<button key={p.k} role="tab" aria-selected={pestana === p.k} className={pestana === p.k ? "on" : ""} onClick={() => ir(p.k)}>{p.t}</button>
				))}
			</div>
			<Aviso texto={error} onClose={() => setError(null)} />
			{pestana === "tablero" ? <Tablero sh={sh} /> : null}
			{pestana === "equipo" ? <MiEquipo sh={sh} /> : null}
			{pestana === "llamadas" ? <Llamadas sh={sh} /> : null}
			{pestana === "envivo" ? <EnVivo sh={sh} /> : null}
			{pestana === "extensiones" ? <Extensiones sh={sh} /> : null}
			{pestana === "departamentos" ? <Departamentos sh={sh} /> : null}
			{pestana === "recepcion" ? <Recepcion sh={sh} /> : null}
			{pestana === "campanas" ? <CampanasAuto sh={sh} /> : null}
			{pestana === "ajustes" && cat.super ? <Ajustes sh={sh} /> : null}
		</div>
	);
};

export default TelefoniaController;
